/**
 * Procedural Alert Sound Generator
 * 
 * Creates attention-grabbing alert sounds for notifications.
 * Designed to be noticeable but not jarring.
 */

import { PROCEDURAL_PARAMS } from '../audioConfig.js';

/**
 * Create and play an alert sound (double pulse)
 * @param {AudioContext} ctx - Web Audio context
 * @param {GainNode} destination - Destination gain node
 */
export function createAlertSound(ctx, destination) {
  if (!ctx || !destination) return;

  const params = PROCEDURAL_PARAMS.alert;
  const now = ctx.currentTime;

  for (let i = 0; i < params.pulses; i++) {
    const startTime = now + (i * params.pulseGap);

    const oscillator = ctx.createOscillator();
    oscillator.type = params.type;
    oscillator.frequency.setValueAtTime(params.frequency, startTime);

    const gainNode = ctx.createGain();
    gainNode.gain.setValueAtTime(0, startTime);
    gainNode.gain.linearRampToValueAtTime(0.25, startTime + 0.005);
    gainNode.gain.setValueAtTime(0.25, startTime + params.duration * 0.5);
    gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + params.duration);

    oscillator.connect(gainNode);
    gainNode.connect(destination);

    oscillator.start(startTime);
    oscillator.stop(startTime + params.duration + 0.01);

    oscillator.onended = () => {
      oscillator.disconnect();
      gainNode.disconnect();
    };
  }
}

/**
 * Create a warning alert (lower pitch, more urgent)
 */
export function createWarningSound(ctx, destination) {
  if (!ctx || !destination) return;

  const now = ctx.currentTime;
  const pulses = 3;
  const pulseGap = 0.15;
  const duration = 0.12;

  for (let i = 0; i < pulses; i++) {
    const startTime = now + (i * pulseGap);

    const oscillator = ctx.createOscillator();
    oscillator.type = 'sawtooth';
    oscillator.frequency.setValueAtTime(440, startTime);
    oscillator.frequency.linearRampToValueAtTime(400, startTime + duration);

    const gainNode = ctx.createGain();
    gainNode.gain.setValueAtTime(0, startTime);
    gainNode.gain.linearRampToValueAtTime(0.15, startTime + 0.01);
    gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + duration);

    // Harsh filter for warning character
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(1200, startTime);

    oscillator.connect(filter);
    filter.connect(gainNode);
    gainNode.connect(destination);

    oscillator.start(startTime);
    oscillator.stop(startTime + duration + 0.01);

    oscillator.onended = () => {
      oscillator.disconnect();
      filter.disconnect();
      gainNode.disconnect();
    };
  }
}

/**
 * Create error alert (dissonant, attention-grabbing)
 */
export function createErrorSound(ctx, destination) {
  if (!ctx || !destination) return;

  const now = ctx.currentTime;
  const duration = 0.2;

  // Dissonant interval (minor second)
  const frequencies = [440, 466.16];

  frequencies.forEach((freq) => {
    const oscillator = ctx.createOscillator();
    oscillator.type = 'square';
    oscillator.frequency.setValueAtTime(freq, now);

    const gainNode = ctx.createGain();
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(0.1, now + 0.01);
    gainNode.gain.setValueAtTime(0.1, now + duration * 0.7);
    gainNode.gain.exponentialRampToValueAtTime(0.001, now + duration);

    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(800, now);

    oscillator.connect(filter);
    filter.connect(gainNode);
    gainNode.connect(destination);

    oscillator.start(now);
    oscillator.stop(now + duration + 0.01);

    oscillator.onended = () => {
      oscillator.disconnect();
      filter.disconnect();
      gainNode.disconnect();
    };
  });
}
